import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';

import { BackIcon } from '../iconRef/mealPLanIcons';

type AlertState = {
  open: boolean;
  severity: 'success' | 'error' | 'info' | 'warning';
  message: string;
};

const BluetoothConnection: React.FC = () => {
  const [ssidList, setSsidList] = useState<string[]>([]);
  const [selectedSsid, setSelectedSsid] = useState('');
  const [password, setPassword] = useState('');
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    severity: 'success',
    message: '',
  });

  const showAlert = (severity: AlertState['severity'], message: string) => {
    setAlertState({ open: true, severity, message });
  };
  const handleAlertClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return;
    setAlertState((s) => ({ ...s, open: false }));
  };

  function postToNative(cmd: string, data: Record<string, any> = {}) {
    const bridge = (window as any).webkit?.messageHandlers?.ble;
    if (!bridge) {
      console.warn('Native bridge not available:', cmd, data);
      return showAlert('error', `Native bridge not available: ${cmd}`);
    }
    bridge.postMessage({ cmd, ...data });
  }

  // Hook up native callbacks once
  useEffect(() => {
    const w = window as any;
    w.onResponse = (action: string, response: string) => {
      switch (action) {
        case 'deviceName':
          showAlert('success', `Device name is ${response}`);
          break;
        case 'connected':
          showAlert('info', '✅ Device connected, now scanning Wi-Fi list…');
          break;
        case 'error':
          showAlert('error', `❌ Error occurred: ${response}`);
          break;
        default:
          console.warn('Unknown onResponse action', action, response);
      }
    };
    w.onWifiList = (list: string[]) => setSsidList(list);
    w.onProvisioningComplete = () =>
      showAlert('success', 'Provisioning successful! 🎉');
    w.onProvisioningError = (msg: string) =>
      showAlert('error', `Provisioning failed: ${msg}`);

    return () => {
      delete w.onResponse;
      delete w.onWifiList;
      delete w.onProvisioningComplete;
      delete w.onProvisioningError;
    };
  }, []);

  const handleStartProvision = () => {
    postToNative('scanQRCode');
  };
  const handleProvision = () => {
    postToNative('provision', { ssid: selectedSsid, password });
    setSelectedSsid('');
    setPassword('');
    setSsidList([]);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          boxShadow: 1,
          mb: 3,
        }}
      >
        <IconButton edge="start" onClick={() => window.history.back()}>
          <BackIcon />
        </IconButton>
        <Typography sx={{ ml: 1 }} variant="h6">
          Bluetooth Connection
        </Typography>
      </Box>

      {/* Intro */}
      <Box sx={{ px: 2 }}>
        <Typography gutterBottom variant="h5">
          Bluetooth Connection
        </Typography>
        <Typography gutterBottom variant="body1">
          Manage your Bluetooth connections here.
        </Typography>

        <Grid container spacing={2}>
          <Grid item>
            <Button onClick={handleStartProvision} variant="contained">
              Scan QR Code
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Provisioning Dialog */}
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => {
          setSsidList([]);
          setSelectedSsid('');
          setPassword('');
        }}
        open={ssidList.length > 0}
      >
        <DialogTitle>Provision Wi-Fi</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography sx={{ mb: 1 }} variant="subtitle2">
                Select Wi-Fi Network:
              </Typography>
              <TextField
                fullWidth
                onChange={(e) => setSelectedSsid(e.target.value)}
                select
                size="small"
                value={selectedSsid}
              >
                {ssidList.map((ssid) => (
                  <MenuItem key={ssid} value={ssid}>
                    {ssid}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Wi-Fi Password"
                onChange={(e) => setPassword(e.target.value)}
                size="small"
                type="password"
                value={password}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            onClick={() => {
              setSsidList([]);
              setSelectedSsid('');
              setPassword('');
            }}
            sx={{ fontSize: '0.875rem' }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleProvision}
            sx={{ fontSize: '0.875rem' }}
            variant="contained"
          >
            Provision Wi-Fi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={2500}
        onClose={handleAlertClose}
        open={alertState.open}
      >
        <Alert
          onClose={handleAlertClose}
          severity={alertState.severity}
          variant="filled"
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BluetoothConnection;
